import { getJWTToken } from '../utils/auth';
import { saveThresholds, type ThresholdStore, type Thresholds } from './thresholds';
import { authHeaders } from './impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

function asThresholds(body: unknown): Thresholds {
  const t = body as Record<string, unknown> | null;
  if (
    !t ||
    typeof t.newcomerMaxYears !== 'number' ||
    typeof t.ltelMinYears !== 'number' ||
    typeof t.monitoringYears !== 'number'
  ) {
    throw new Error('generation-settings response is missing a threshold');
  }
  return {
    newcomerMaxYears: t.newcomerMaxYears,
    ltelMinYears: t.ltelMinYears,
    monitoringYears: t.monitoringYears,
  };
}

export async function fetchGenerationSettings(): Promise<Thresholds> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/settings/generation`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`fetch-generation-settings failed: ${response.status}`);
  }
  return asThresholds(await response.json());
}

export async function putGenerationSettings(thresholds: Thresholds): Promise<void> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/settings/generation`, {
    method: 'PUT',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(thresholds),
  });
  if (!response.ok) {
    throw new Error(`save-generation-settings failed: ${response.status}`);
  }
}

let sessionEpoch = 0;
let hydrateSeq = 0;

// Sign-out and account switches must not be overwritten by a hydrate the
// previous session had in flight.
export function resetThresholdsSession(): void {
  sessionEpoch += 1;
  hydrateSeq += 1;
}

export async function refreshThresholds(store?: ThresholdStore | null): Promise<Thresholds | null> {
  const epoch = sessionEpoch;
  // Overlapping refreshes can resolve out of order; the older must not win.
  const seq = ++hydrateSeq;
  const thresholds = await fetchGenerationSettings();
  if (epoch !== sessionEpoch || seq !== hydrateSeq) return null;
  const cached = store === undefined ? saveThresholds(thresholds) : saveThresholds(thresholds, store);
  if (!cached) console.warn('generation-settings cached copy not written; this browser renders default thresholds');
  return thresholds;
}

export async function hydrateThresholds(store?: ThresholdStore | null): Promise<void> {
  try {
    await refreshThresholds(store);
  } catch (err) {
    // Keeping the stale cache beats writing defaults the server would not use.
    console.warn('generation-settings hydrate failed; keeping the cached thresholds', err);
  }
}
